import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLogo } from '@/hooks/useLogo';
import canyonImage from '@/assets/login-canyon.jpg';
import forestImage from '@/assets/login-forest.jpg';
import coastImage from '@/assets/login-coast.jpg';

export const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const { login, recoverPassword } = useAuth();
  const navigate = useNavigate();
  const { logoSvgUrl } = useLogo();

  // Randomly select background image on component mount
  const [backgroundImage] = useState(() => {
    const images = [canyonImage, forestImage, coastImage];
    return images[Math.floor(Math.random() * images.length)];
  });

  // Calculate blur amount based on username length (starts at 50px, reduces by 10px per character)
  const blurAmount = Math.max(0, 50 - (username.length * 10));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(username, password);
    
    if (result.error) {
      setError(result.error);
    } else {
      setLoginSuccess(true);
      // ProtectedRoute will handle profile selection if useDWProfiles is enabled
    }

    setIsLoading(false);
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await recoverPassword(recoveryEmail);
    
    if (result.error) {
      setError(result.error);
    } else {
      setRecoverySuccess(true);
    }

    setIsLoading(false);
  };

  if (showRecovery) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: `blur(${blurAmount}px)`,
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: `blur(${blurAmount}px)`,
          }}
        />
        <div className="relative z-10 w-full max-w-md">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-lg -z-10"></div>
        <Card className={`w-full bg-[#0066cc] text-white border-none shadow-2xl backdrop-blur-sm rounded-lg transition-all duration-500 ${loginSuccess ? 'blur-sm opacity-50' : ''}`}>
        <CardHeader className="space-y-1 p-0 rounded-t-lg">
          <div className="flex justify-center mb-6 py-4">
            <img 
              src={logoSvgUrl} 
              alt="Logo" 
              className="w-64 h-40 object-contain"
              width={256}
              height={160}
              fetchPriority="high"
            />
          </div>
          <div className="px-6">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10"
                onClick={() => {
                  setShowRecovery(false);
                  setRecoverySuccess(false);
                  setError('');
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-2xl text-white">Password Recovery</CardTitle>
            </div>
            <CardDescription className="mt-2 text-white/80">
              Enter your email address to receive password recovery instructions
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
            {recoverySuccess ? (
              <Alert className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Password recovery instructions have been sent to your email address.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleRecovery} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="recovery-email" className="text-white">Email Address</Label>
                  <Input
                    id="recovery-email"
                    type="email"
                    placeholder="Enter your email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    className="text-black rounded-md"
                    required
                  />
                </div>

                <Button type="submit" className="w-full bg-white text-[#0066cc] hover:bg-white/90 rounded-md" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Recovery Email'}
                </Button>
              </form>
            )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
    >
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: `blur(${blurAmount}px)`,
        }}
      />
      <div className="relative z-10 w-full max-w-md">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-md rounded-lg -z-10"></div>
        <Card className={`w-full bg-black/50 text-white border-none shadow-2xl backdrop-blur-md rounded-lg transition-all duration-500 ${loginSuccess ? 'blur-sm opacity-50' : ''}`}>
        <CardHeader className="space-y-1 p-0 rounded-t-lg">
          <div className="flex justify-center mb-6 py-4 relative">
            <div className="relative z-20">
              <img 
                src="/lovable-uploads/roadhaven-logo-light.svg"
                alt="Logo" 
                className="w-64 h-40 object-contain"
                width={256}
                height={160}
                fetchPriority="high"
                style={{ filter: 'none', backdropFilter: 'none' }}
              />
            </div>
          </div>
          <div className="px-6">
            <CardTitle className="text-2xl text-center text-white">Dealer Portal</CardTitle>
            <CardDescription className="text-center text-white/80">
              Enter your credentials to access your account
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="bg-destructive/90 text-destructive-foreground mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-white">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="text-black rounded-md"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="text-black rounded-md"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                variant="link"
                className="px-0 text-sm text-white/80 hover:text-white"
                onClick={() => setShowRecovery(true)}
              >
                Forgot password?
              </Button>
            </div>

            <Button type="submit" className="w-full bg-white text-[#0066cc] hover:bg-white/90 rounded-md" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
